"use client";

import { useEffect, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import {
  Box,
  Image,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  useDisclosure,
  Alert,
  AlertIcon,
  Link,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonCheckTable from "common/components/checktable";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";
import { globalStyles } from "common/theme/styles";
import DateFormatter from "common/utils/dateFormater";
import viewIcon from "common/assets/img/viewIcon.png";
import loiService from "services/Recruit/services/loi";
import { getStatusBadgeStyle } from "common/utils/statusColor";
import { formatStatus } from "services/Crm/utils/calcUtils";
import { getApi, deleteManyApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import documentVerificationService from "services/Recruit/services/documentVerification";
import emailTemplateService from "services/Recruit/services/emailTemplate";
import TextEditor from "common/components/textEditor/TextEditor";
import CommonDeleteModel from "common/components/commonDeleteModel";
import {
  getTableFilters,
  saveTableFilters,
  clearTableFilters,
  getInitialFilterState
} from "common/utils/filterUtils";

const LOI = () => {
  const title = "Letter of Intent (LOI)";
  const TABLE_NAME = 'loi'; // Define table name constant
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [deleteModel, setDelete] = useState(false);
  const [displaySearchData, setDisplaySearchData] = useState(false);
  const [searchedData, setSearchedData] = useState([]);
  const colors = globalStyles.colors;
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchbox, setSearchbox] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.searchbox || "";
  });
  const [finalSearchedData, setFinalSearchedData] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [permission] = HasAccess([accessModuleTitles.Applications]); // Using Applications permission for now
  const { isOpen: isEmailViewOpen, onOpen: onEmailViewOpen, onClose: onEmailViewClose } = useDisclosure();
  const [selectedEmailPreview, setSelectedEmailPreview] = useState(null);

  // Create modal state
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const [applications, setApplications] = useState([]);
  const [isApplicationsLoading, setIsApplicationsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    applicationId: '',
    emailTemplateId: '',
    subject: 'Letter of Intent',
    content: '',
  });

  // Status filter options
  const loiStatusOptions = [
    { value: "", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "viewed", label: "Viewed" },
    { value: "accepted", label: "Accepted" },
    { value: "rejected", label: "Rejected" },
    { value: "expired", label: "Expired" },
    { value: "on_hold", label: "On Hold" },
    { value: "withdrawn", label: "Withdrawn" },
  ];

  const [currentStatusFilter, setCurrentStatusFilter] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.currentStatusFilter || "";
  });

  // Save filters whenever they change
  useEffect(() => {
    const filters = {
      currentStatusFilter,
      searchbox: searchbox || "",
    };
    saveTableFilters(TABLE_NAME, filters);
  }, [currentStatusFilter, searchbox]);

  const handleStatusFilterChange = useCallback((selectedStatus) => {
    setCurrentStatusFilter(selectedStatus);
    setCurrentPage(1);
  }, []);

  // Fetch applications with approved document verification for create modal
  useEffect(() => {
    const fetchApplications = async () => {
      setIsApplicationsLoading(true);
      try {
        const [appsResult, docVerificationsResult] = await Promise.all([
          getApi("api/recruitment/application/?allData=true"),
          documentVerificationService.listDocumentVerifications({ allData: true })
        ]);

        const allApps = appsResult?.data?.data || [];
        const approvedDocVerifications = (docVerificationsResult?.data?.data || [])
          .filter(dv => dv.verificationStatus === 'approved')
          .map(dv => dv.application?._id || dv.application);

        // Filter applications to only those with approved document verification
        const eligibleApps = allApps.filter(app =>
          approvedDocVerifications.includes(app._id)
        );

        setApplications(eligibleApps);
      } catch (e) {
        console.error("Failed to fetch applications:", e);
        toast.error("Failed to load applications");
      } finally {
        setIsApplicationsLoading(false);
      }
    };

    const fetchEmailTemplates = async () => {
      setIsTemplatesLoading(true);
      try {
        const response = await emailTemplateService.getTemplates({ allData: true, isActive: true });
        const templates = response?.data?.data ?? response?.data ?? [];
        setEmailTemplates(Array.isArray(templates) ? templates : []);
      } catch (e) {
        console.error("Failed to fetch email templates:", e);
        setEmailTemplates([]);
      } finally {
        setIsTemplatesLoading(false);
      }
    };

    fetchApplications();
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    fetchData(
      1,
      pageSize,
      sortColumn,
      sortOrder,
      searchbox || "",
      currentStatusFilter || ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageSize,
    currentStatusFilter,
    sortColumn,
    sortOrder,
  ]);

  const fetchData = async (
    page = currentPage,
    limit = pageSize,
    sortKey = sortColumn,
    order = sortOrder,
    searchTerm = "",
    status = ""
  ) => {
    setIsLoading(true);

    const params = { page, limit };

    if (sortKey) {
      params.sort = order === "desc" ? `-${sortKey}` : sortKey;
    }
    if (searchTerm && searchTerm.trim() !== "") {
      params.search = searchTerm.trim();
    }
    if (status && status.trim() !== "") {
      params.status = status.trim();
    }

    try {
      const response = await loiService.listLOIs(params);
      const resultData = response?.data?.data ?? [];
      const resultMeta = response?.data?.metadata ?? {};

      const baseIndex = (page - 1) * limit;
      const formattedData = (resultData || []).map((item, index) => ({
        _id: item._id,
        srNo: baseIndex + index + 1,
        applicationId: item.application?.customId || "-",
        candidateName: item.candidate?.name || "-",
        candidateEmail: item.candidate?.email || "-",
        subject: item.subject || "-",
        status: item.status || "draft",
        sentAt: item.sentAt ? DateFormatter({ date: item.sentAt }) : "-",
        viewedAt: item.viewedAt ? DateFormatter({ date: item.viewedAt }) : "-",
        respondedAt: item.respondedAt
          ? DateFormatter({ date: item.respondedAt })
          : "-",
        candidateResponse: item.candidateResponse?.status || "-",
        lastUpdated: DateFormatter({ date: item.updatedAt || item.createdAt }),
        createdBy:
          `${item.createBy?.firstName || ""} ${item.createBy?.lastName || ""}`.trim() || "-",
        emailTemplateName:
          item.templateUsed?.name ||
          item.emailTemplateId?.name ||
          item.emailTemplate?.name ||
          item.templateName ||
          "",
        rawData: item,
        application: item.application,
      }));

      setData(formattedData);
      setMetadata(resultMeta);
    } catch (error) {
      toast.error("Failed to fetch LOIs");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteData = async (ids) => {
    try {
      setDelete(false);
      let response = await deleteManyApi(API_PATHS.RECRUITMENT_LOI_DELETE_MANY, ids);
      if (response.status === 200) {
        setSelectedValues([]);
        fetchData(currentPage, pageSize);
        toast.success(
          `${selectedValues && selectedValues.length > 1 ? "LOIs" : "LOI"} deleted successfully`
        );
      }
    } catch (error) {
      toast.error("Failed to delete LOI(s)");
      console.error("Error deleting LOI(s):", error);
    }
  };

  const handleSortChange = useCallback((column, order) => {
    setSortColumn(column);
    setSortOrder(order);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((searchTerm) => {
    const trimmedTerm = searchTerm?.trim() || "";
    setSearchbox(trimmedTerm);
    setCurrentPage(1);
  }, []);

  const handleEmailPreviewOpen = (rowData) => {
    setSelectedEmailPreview(rowData);
    onEmailViewOpen();
  };

  const onLinkClick = async (id) => {
    const item = data.find((d) => d._id === id);
    if (!item) {
      console.error('Item not found for id:', id);
      return;
    }

    // Extract Application ID
    let applicationId = null;

    if (item.application && item.application._id) {
      applicationId = item.application._id;
    } else if (item.rawData?.application?._id) {
      applicationId = item.rawData.application._id;
    } else if (item.rawData?.application && typeof item.rawData.application === 'string') {
      applicationId = item.rawData.application;
    }

    if (applicationId) {
      navigate(`/recruitment/application-create/${applicationId}`, {
        state: { activeTab: 3 }, // LOI tab index
      });
      return;
    }

    // Fallback: If no Application ID, try to find it via Candidate ID
    console.log('No Application ID found directly. Trying to fetch from Candidate...');

    // Extract Candidate ID
    let candidateId = null;
    if (item?.candidate?._id) {
      candidateId = item.candidate._id;
    } else if (item?.rawData?.candidate?._id) {
      candidateId = item.rawData.candidate._id;
    } else if (item?.rawData?.candidate) {
      candidateId = typeof item.rawData.candidate === 'string'
        ? item.rawData.candidate
        : item.rawData.candidate._id || item.rawData.candidate.toString();
    }

    if (candidateId) {
      // Ensure string
      const candidateIdStr = typeof candidateId === 'string' ? candidateId : (candidateId._id || candidateId.toString() || String(candidateId));

      if (candidateIdStr && candidateIdStr !== '[object Object]') {
        try {
          setIsLoading(true); // Show loading indicator
          const response = await getApi(`${API_PATHS.CANDIDATE_LIST}${candidateIdStr}/applications`);
          const applications = response?.data?.data || [];

          if (applications.length > 0) {
            // Sort by createdAt desc to get the latest
            const latestApp = applications.sort((a, b) =>
              new Date(b.createdAt) - new Date(a.createdAt)
            )[0];

            if (latestApp && latestApp._id) {
              toast.info('Redirecting to the latest application for this candidate.');
              navigate(`/recruitment/application-create/${latestApp._id}`, {
                state: { activeTab: 3 },
              });
            } else {
              toast.warning('Found candidate applications but could not determine ID.');
            }
          } else {
            toast.warning('No applications found for this candidate. Please create an application first.');
          }
        } catch (error) {
          console.error('Error fetching applications for candidate:', error);
          toast.error('Failed to find associated application.');
        } finally {
          setIsLoading(false);
        }
      } else {
        console.error('Invalid candidateId:', candidateId);
        toast.error('Unable to navigate: Invalid candidate ID');
      }
    } else {
      console.error('No Application ID and no Candidate ID found for item:', item);
      toast.error('Unable to navigate: Record data is incomplete');
    }
  };

  // Helper function to replace placeholders in content
  const replacePlaceholders = (content, candidateName, jobRole) => {
    if (!content) return '';

    const placeholderMap = {
      '[Candidate Name]': candidateName || '[Candidate Name]',
      '[CANDIDATE_NAME]': candidateName || '[Candidate Name]',
      '{{CANDIDATE_NAME}}': candidateName || '[Candidate Name]',
      '[YourName]': candidateName || '[YourName]',
      '[YOUR_NAME]': candidateName || '[YourName]',
      '{{YOUR_NAME}}': candidateName || '[YourName]',
      '[Job Role]': jobRole || '[Job Role]',
      '[JOB_ROLE]': jobRole || '[Job Role]',
      '{{JOB_TITLE}}': jobRole || '[Job Role]',
      '{{JOB_ROLE}}': jobRole || '[Job Role]',
    };

    let replacedContent = content;
    Object.entries(placeholderMap).forEach(([placeholder, value]) => {
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      replacedContent = replacedContent.replace(regex, value);
    });

    return replacedContent;
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      setCreateForm({
        ...createForm,
        emailTemplateId: '',
        subject: 'Letter of Intent',
        content: '',
      });
      return;
    }

    const template = emailTemplates.find(t => t._id === templateId);
    if (template) {
      // Get candidate name and job role from selected application
      const selectedApp = applications.find(app => app._id === createForm.applicationId);
      const candidateName = selectedApp?.candidateDetails?.name || selectedApp?.candidate?.name || '';
      const jobRole = selectedApp?.job?.postingTitle || '';

      // Warn if template is selected before application
      if (!createForm.applicationId || (!candidateName && !jobRole)) {
        toast.warning('Please select an application first to replace placeholders automatically');
      }

      // Replace placeholders in template content
      const replacedContent = replacePlaceholders(
        template.content || '',
        candidateName,
        jobRole
      );

      const replacedSubject = replacePlaceholders(
        template.subject || 'Letter of Intent',
        candidateName,
        jobRole
      );

      setCreateForm({
        ...createForm,
        emailTemplateId: templateId,
        subject: replacedSubject,
        content: replacedContent,
      });

      if (candidateName && jobRole) {
        toast.success('Email template applied with placeholders replaced');
      } else {
        toast.info('Email template applied. Select an application to replace placeholders.');
      }
    }
  };

  const handleApplicationSelect = (applicationId) => {
    const selectedApp = applications.find(app => app._id === applicationId);
    if (selectedApp && createForm.emailTemplateId) {
      // If a template is already selected, replace placeholders in existing content
      const candidateName = selectedApp.candidateDetails?.name || selectedApp.candidate?.name || '';
      const jobRole = selectedApp.job?.postingTitle || '';

      const template = emailTemplates.find(t => t._id === createForm.emailTemplateId);
      if (template) {
        const replacedContent = replacePlaceholders(
          template.content || '',
          candidateName,
          jobRole
        );

        const replacedSubject = replacePlaceholders(
          template.subject || 'Letter of Intent',
          candidateName,
          jobRole
        );

        setCreateForm({
          ...createForm,
          applicationId,
          subject: replacedSubject,
          content: replacedContent,
        });
      }
    } else {
      setCreateForm({ ...createForm, applicationId });
    }
  };

  const handleCreateLOI = async () => {
    if (!createForm.applicationId) {
      toast.error("Please select an application");
      return;
    }
    if (!createForm.subject || !createForm.content) {
      toast.error("Please fill in subject and content");
      return;
    }

    setIsCreating(true);
    try {
      const response = await loiService.createLOI({
        applicationId: createForm.applicationId,
        subject: createForm.subject,
        content: createForm.content,
      });

      toast.success(response?.data?.message || "LOI created successfully");
      onCreateClose();
      setCreateForm({
        applicationId: '',
        emailTemplateId: '',
        subject: 'Letter of Intent',
        content: '',
      });
      // Refresh the list
      fetchData(
        currentPage,
        pageSize,
        sortColumn,
        sortOrder,
        searchbox || "",
        currentStatusFilter || ""
      );
    } catch (error) {
      console.error("Error creating LOI:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to create LOI");
    } finally {
      setIsCreating(false);
    }
  };

  const tableColumns = [
    {
      Header: "#",
      accessor: "_id",
      isSortable: false,
      width: 5,
    },
    {
      Header: "Application ID",
      accessor: "applicationId",
      isSortable: true,
    },
    {
      Header: "Candidate Name",
      accessor: "candidateName",
      isSortable: true,
    },
    {
      Header: "Email",
      accessor: "content",
      isSortable: false,
      cell: ({ row }) => {
        const hasEmail = row?.original?.subject || row?.original?.rawData?.content || row?.original?.emailTemplateName;
        if (!hasEmail) return <Text>-</Text>;

        return (
          <Link color="blue.500" fontWeight="600" onClick={() => handleEmailPreviewOpen(row.original)}>
            View Email
          </Link>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      isSortable: true,
      cell: ({ row }) => {
        const status = row?.values?.status || "draft";
        const badgeStyle = getStatusBadgeStyle(status);
        return (
          <Box display="flex" alignItems="center" gap={2}>
            <Box bg={badgeStyle.color} h="8px" w="8px" borderRadius="full" />
            <Text as="span" color={badgeStyle.color} style={{ color: badgeStyle.color }} fontWeight="500" whiteSpace="nowrap">
              {formatStatus(status)}
            </Text>
          </Box>
        );
      },
    },
    {
      Header: "Sent At",
      accessor: "sentAt",
      isSortable: true,
    },
    {
      Header: "Viewed At",
      accessor: "viewedAt",
      isSortable: true,
    },
    {
      Header: "Responded At",
      accessor: "respondedAt",
      isSortable: true,
    },
    {
      Header: "Candidate Response",
      accessor: "candidateResponse",
      isSortable: false,
      cell: ({ row }) => {
        const response = row?.values?.candidateResponse;
        if (!response || response === "-") return <Text>-</Text>;
        const badgeStyle = getStatusBadgeStyle(response);
        return (
          <Box display="flex" alignItems="center" gap={2}>
            <Box bg={badgeStyle.color} h="8px" w="8px" borderRadius="full" />
            <Text as="span" color={badgeStyle.color} style={{ color: badgeStyle.color }} fontWeight="500" whiteSpace="nowrap">
              {formatStatus(response)}
            </Text>
          </Box>
        );
      },
    },
    {
      Header: "Last Updated",
      accessor: "lastUpdated",
      isSortable: true,
    },
  ];

  const actionHeader = {
    Header: "Actions",
    isSortable: false,
    center: true,
    cell: ({ row }) => (
      <Text fontSize="md" fontWeight="900" textAlign={"center"}>
        <Box className="actionCellContainer">
          {permission?.view && (
            <Image
              onClick={() => onLinkClick(row?.original?._id)}
              src={viewIcon}
              h={"20px"}
              w={"20px"}
              cursor="pointer"
            />
          )}
        </Box>
      </Text>
    ),
  };

  const dataColumn = [...tableColumns];

  return (
    <div>
      <CommonCheckTable
        title={title}
        showInlineTitle={true}
        isLoading={isLoading}
        columnData={tableColumns}
        dataColumn={dataColumn}
        allData={data}
        onOpen={onCreateOpen}
        tableData={data}
        setSearchDisplay={setDisplaySearchData}
        tableCustomFields={[]}
        access={permission}
        action={true}
        setAction={() => { }}
        setDelete={setDelete}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        fetchData={fetchData}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        searchboxOutside={searchbox}
        sortOrder={sortOrder}
        searchbox={searchbox}
        setSearchbox={setSearchbox}
        finalSearchedData={finalSearchedData}
        setFinalSearchedData={setFinalSearchedData}
        metadata={metadata}
        handleSortChange={handleSortChange}
        handleSearchChange={handleSearchChange}
        filterStatusOptions={loiStatusOptions}
        statusFilter={true}
        handleStatusFilterChange={handleStatusFilterChange}
        sortColumn={sortColumn}
        currentStatusFilter={currentStatusFilter}
        onLinkClick={(id) => { onLinkClick(id); }}
        isDynamic={true}
      />

      {/* Create LOI Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Letter of Intent (LOI)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Only applications with approved document verification are shown.
              </Alert>

              <FormControl isRequired>
                <FormLabel>Application</FormLabel>
                <Select
                  placeholder={isApplicationsLoading ? "Loading applications..." : "Select an application"}
                  value={createForm.applicationId}
                  onChange={(e) => handleApplicationSelect(e.target.value)}
                  isDisabled={isApplicationsLoading}
                >
                  {applications.length === 0 && !isApplicationsLoading ? (
                    <option value="" disabled>No applications with approved document verification</option>
                  ) : (
                    applications
                      .filter((app) =>
                        app?.job?.deleted !== true &&
                        app?.candidateDetails?.deleted !== true
                      )
                      .map((app) => {
                        const candidateName = app.candidateDetails?.name || "Unknown";
                        const jobTitle = app.job?.postingTitle || "Unknown";
                        const appId = app.customId || app._id;
                        return (
                          <option key={app._id} value={app._id}>
                            {candidateName} - {jobTitle} ({appId})
                          </option>
                        );
                      })
                  )}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Email Template (Optional)</FormLabel>
                <Select
                  placeholder={isTemplatesLoading ? "Loading templates..." : "Select an email template"}
                  value={createForm.emailTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  isDisabled={isTemplatesLoading}
                >
                  <option value="">None - Use custom content</option>
                  {emailTemplates && emailTemplates.length > 0 ? (
                    emailTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {template.name} {template.category ? `(${template.category})` : ''}
                      </option>
                    ))
                  ) : (
                    !isTemplatesLoading && <option value="" disabled>No templates available</option>
                  )}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {isTemplatesLoading
                    ? "Loading templates..."
                    : emailTemplates.length > 0
                      ? `Selecting a template will auto-fill subject and content with placeholders replaced (${emailTemplates.length} templates available)`
                      : "No email templates available. Create templates in the Email Templates section."}
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Subject</FormLabel>
                <Input
                  value={createForm.subject}
                  onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
                  placeholder="Letter of Intent"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Content</FormLabel>
                <TextEditor
                  toolbarId="loi-content-create-list"
                  value={createForm.content}
                  handleChange={(value) => {
                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                    setCreateForm({ ...createForm, content: cleanedValue });
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Available placeholders: [Candidate Name], [Job Role], [YourName]
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onCreateClose} isDisabled={isCreating}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateLOI}
              isLoading={isCreating}
              loadingText="Creating..."
              isDisabled={applications.length === 0}
            >
              Create LOI
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CommonDeleteModel
        isOpen={deleteModel}
        onClose={() => setDelete(false)}
        type={
          selectedValues && selectedValues.length > 1
            ? "LOIs"
            : "LOI"
        }
        handleDeleteData={handleDeleteData}
        ids={selectedValues}
        onCancel={() => setSelectedValues([])}
      />

      <Modal isOpen={isEmailViewOpen} onClose={onEmailViewClose} size="4xl">
        <ModalOverlay />
        <ModalContent w="full" maxW={["95vw", "90vw", "80vw", "4xl"]} maxH="90vh">
          <ModalHeader>Sent Email</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.500">Candidate</Text>
                <Text fontWeight="600">{selectedEmailPreview?.candidateName || "-"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Email</Text>
                <Text fontWeight="600">{selectedEmailPreview?.candidateEmail || "-"}</Text>
              </Box>
              {selectedEmailPreview?.emailTemplateName && (
                <Box>
                  <Text fontSize="sm" color="gray.500">Template</Text>
                  <Text fontWeight="600">{selectedEmailPreview.emailTemplateName}</Text>
                </Box>
              )}
              <Box>
                <Text fontSize="sm" color="gray.500">Subject</Text>
                <Text fontWeight="600">{selectedEmailPreview?.subject || "-"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={2}>Email Content</Text>
                {selectedEmailPreview?.rawData?.content ? (
                  <Box
                    p={4}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmailPreview.rawData.content) }}
                  />
                ) : (
                  <Text color="gray.500">No email content available.</Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onEmailViewClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default LOI;
